/**
 * Sprint 4I1.A — Master Data: Province + Codici IVA
 *
 * Pagina di gestione delle tabelle di lookup globali.
 * Accessibile solo a developer/admin globale (gestione_master_data).
 *
 * Pattern visivo: coerente con PassaggioProprietaSettings.jsx
 * Stili tabella: cardStyles.js (pixel-perfect con altre card).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, isElevated, isDeveloper } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Database, Plus, Pencil, Trash2, Save, ArrowLeft, Download, Search, X, Lock,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  THEAD_WRAPPER, COL_HEADER_LEFT, COL_HEADER_CENTER, CELL_TEXT_DEFAULT,
  CELL_AZIONI, ICON_BTN_EDIT, ICON_BTN_DELETE, WARNING_BANNER_CN,
} from "@/lib/cardStyles";

const IVA_TIPI = [
  { value: "imponibile", label: "Imponibile" },
  { value: "esente", label: "Esente" },
  { value: "non_soggetto", label: "Non soggetto" },
  { value: "non_imponibile", label: "Non imponibile" },
  { value: "regime_margine", label: "Regime margine" },
  { value: "reverse_charge", label: "Reverse charge" },
];

const REGIONI = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto",
];


export default function ElenchiDatabaseSettings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const elevated = isElevated(user);
  const isDev = isDeveloper(user);
  const [tab, setTab] = useState("province");

  if (!elevated) {
    return (
      <Card className="p-6 text-sm text-slate-500 text-center" data-testid="elenchi-db-no-perm">
        Permesso "Gestione master data" richiesto per accedere a questa pagina (admin globale).
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="elenchi-db-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/settings")}
                  data-testid="elenchi-db-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Settings
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Database className="h-6 w-6 text-blue-600" /> Elenchi database
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Gestione delle tabelle di lookup globali (Province ISTAT e Codici IVA).
              I record di sistema non sono eliminabili e hanno codice readonly.
            </p>
          </div>
        </div>
      </div>

      <Card className="p-3">
        <div className="text-[11px] uppercase tracking-wide font-medium text-slate-500 mb-2">Tipologia elenco</div>
        <div className="flex flex-wrap gap-2" data-testid="elenchi-db-tabs">
          <TabButton testId="tab-province" active={tab === "province"}
                     onClick={() => setTab("province")}>Province</TabButton>
          <TabButton testId="tab-codici-iva" active={tab === "codici-iva"}
                     onClick={() => setTab("codici-iva")}>Codici IVA</TabButton>
          {/* Sprint 4H-DEP.B — Depositi di sistema */}
          <TabButton testId="tab-depositi-sistema" active={tab === "depositi-sistema"}
                     onClick={() => setTab("depositi-sistema")}>Depositi di sistema</TabButton>
        </div>
      </Card>

      {tab === "province" && <ProvinceSection isDev={isDev} />}
      {tab === "codici-iva" && <CodiciIvaSection isDev={isDev} />}
      {tab === "depositi-sistema" && <DepositiSistemaSection isDev={isDev} elevated={elevated} />}
    </div>
  );
}


function TabButton({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      data-active={active}
      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors
        ${active
          ? "bg-blue-600 text-white"
          : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
      {children}
    </button>
  );
}


/* ===================================================================
 * Province
 * =================================================================== */
function ProvinceSection({ isDev }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [includeDisattive, setIncludeDisattive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, obj=edit

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      params.set("attivo", includeDisattive ? "all" : "true");
      const r = await api.get(`/master-data/province?${params}`);
      setList(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento province");
    } finally {
      setLoading(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, includeDisattive]);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const remove = async (row) => {
    if (row.is_sistema) {
      toast.error("Provincia di sistema: usa 'Modifica' per disattivare.");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Disattivare la provincia ${row.codice} - ${row.nome}?`)) return;
    try {
      await api.delete(`/master-data/province/${row.codice}`);
      toast.success(`Provincia ${row.codice} disattivata`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore disattivazione");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/master-data/province/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "province.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export CSV");
    }
  };

  return (
    <Card className="p-5" data-testid="province-section">
      <div className="flex items-center justify-between mb-3 pb-2 border-b">
        <h3 className="text-lg font-bold">Province ({list.length})</h3>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca sigla o nome…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[220px]"
              data-testid="province-search"
            />
            {search && (
              <button onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer px-2">
            <input type="checkbox" checked={includeDisattive}
                   onChange={(e) => setIncludeDisattive(e.target.checked)}
                   data-testid="province-toggle-disattive" />
            Mostra disattivate
          </label>
          <Button size="sm" variant="outline" onClick={exportCsv}
                  data-testid="province-export-csv">
            <Download className="h-4 w-4 mr-1" /> Esporta CSV
          </Button>
          <Button size="sm" onClick={openCreate}
                  data-testid="province-add">
            <Plus className="h-4 w-4 mr-1" /> Nuova provincia
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-400">Caricamento…</div>
      ) : (
        <table className="w-full text-sm">
          <thead className={THEAD_WRAPPER}>
            <tr>
              <th className={`${COL_HEADER_LEFT} w-[110px]`}>Sigla</th>
              <th className={COL_HEADER_LEFT}>Nome</th>
              <th className={COL_HEADER_LEFT}>Regione</th>
              <th className={`${COL_HEADER_CENTER} w-[90px]`}>Attiva</th>
              <th className={`${COL_HEADER_CENTER} w-[100px]`}>Sistema</th>
              <th className={`${COL_HEADER_CENTER} w-[80px]`}>Ordine</th>
              <th className="w-[80px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((r) => (
              <tr key={r.codice} data-testid={`province-row-${r.codice}`}
                  className={r.attivo ? "" : "opacity-60"}>
                <td className={`${CELL_TEXT_DEFAULT} font-mono font-semibold`}>{r.codice}</td>
                <td className={CELL_TEXT_DEFAULT}>{r.nome}</td>
                <td className={`${CELL_TEXT_DEFAULT} text-slate-600`}>{r.regione}</td>
                <td className={`${CELL_TEXT_DEFAULT} text-center`}>
                  {r.attivo
                    ? <span className="text-emerald-600">●</span>
                    : <span className="text-slate-400">○</span>}
                </td>
                <td className={`${CELL_TEXT_DEFAULT} text-center`}>
                  {r.is_sistema
                    ? <Lock className="h-3.5 w-3.5 text-slate-500 inline" />
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className={`${CELL_TEXT_DEFAULT} text-center text-slate-500`}>
                  {r.ordine_display ?? 0}
                </td>
                <td className={CELL_AZIONI}>
                  <button onClick={() => openEdit(r)}
                          className={ICON_BTN_EDIT}
                          title="Modifica"
                          data-testid={`province-edit-${r.codice}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(r)}
                          disabled={r.is_sistema}
                          className={`${ICON_BTN_DELETE} ${r.is_sistema ? "opacity-30 cursor-not-allowed" : ""}`}
                          title={r.is_sistema ? "Provincia di sistema: non eliminabile" : "Disattiva"}
                          data-testid={`province-delete-${r.codice}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-slate-400 italic">
                Nessuna provincia trovata.
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <ProvinciaModal
          row={editing}
          isDev={isDev}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}
    </Card>
  );
}


function ProvinciaModal({ row, isDev, onClose, onSaved }) {
  const isEdit = !!row;
  const isSistema = !!row?.is_sistema;
  // Sprint 4I1.A.1: developer può modificare TUTTI i campi anche su record sistema
  const canEditAllFields = !isSistema || isDev;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState({
    codice: row?.codice || "",
    nome: row?.nome || "",
    regione: row?.regione || "",
    attivo: row?.attivo ?? true,
    ordine_display: row?.ordine_display ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Sprint 4I1.A.1: per record sistema + developer serve una conferma extra
  const requiresExtraConfirm = isEdit && isSistema && isDev;

  const doSubmit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        let payload;
        if (isSistema && isDev) {
          // Developer su sistema: full edit (incluso codice via rename)
          payload = {
            nome: form.nome, regione: form.regione,
            attivo: form.attivo, ordine_display: form.ordine_display,
          };
          if (form.codice && form.codice !== row.codice) {
            payload.codice = form.codice;
          }
        } else if (isSistema) {
          payload = { attivo: form.attivo, ordine_display: form.ordine_display };
        } else {
          payload = {
            nome: form.nome, regione: form.regione,
            attivo: form.attivo, ordine_display: form.ordine_display,
          };
        }
        await api.patch(`/master-data/province/${row.codice}`, payload);
        toast.success(`Provincia ${row.codice} aggiornata`);
      } else {
        if (!form.codice.trim() || !form.nome.trim() || !form.regione.trim()) {
          toast.error("Compila tutti i campi obbligatori");
          setSaving(false);
          return;
        }
        await api.post("/master-data/province", form);
        toast.success(`Provincia ${form.codice.toUpperCase()} creata`);
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore salvataggio");
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const submit = async () => {
    if (requiresExtraConfirm) {
      setConfirmOpen(true);
      return;
    }
    await doSubmit();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
         data-testid="province-modal">
      <Card className="w-[480px] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="text-lg font-bold">
            {isEdit ? "Modifica provincia" : "Nuova provincia"}
          </h3>
          {isSistema && (
            <span className="text-[11px] text-slate-500 flex items-center gap-1">
              <Lock className="h-3 w-3" /> Sistema
            </span>
          )}
        </div>

        {isSistema && isDev && (
          <div className={WARNING_BANNER_CN} data-testid="province-modal-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <strong>Stai modificando un record di sistema.</strong> Le modifiche
              impatteranno tutti i record collegati a questo codice. Procedi con cautela.
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Sigla *</Label>
            <Input
              value={form.codice}
              onChange={(e) => setF({ codice: e.target.value.toUpperCase().slice(0, 4) })}
              placeholder="TO"
              disabled={isEdit && !isDev}
              maxLength={4}
              data-testid="province-form-codice"
              className="font-mono"
            />
            {isEdit && !isDev && (
              <p className="text-[11px] text-slate-400 mt-0.5">Non modificabile</p>
            )}
            {isEdit && isSistema && isDev && (
              <p className="text-[11px] text-amber-700 mt-0.5">Rename consentito (developer)</p>
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Nome *</Label>
            <Input
              value={form.nome}
              onChange={(e) => setF({ nome: e.target.value })}
              placeholder="Torino"
              disabled={!canEditAllFields}
              data-testid="province-form-nome"
            />
          </div>
        </div>

        <div>
          <Label className="text-xs">Regione *</Label>
          <Select
            value={form.regione}
            onValueChange={(v) => setF({ regione: v })}
            disabled={!canEditAllFields}>
            <SelectTrigger data-testid="province-form-regione">
              <SelectValue placeholder="Seleziona regione…" />
            </SelectTrigger>
            <SelectContent>
              {REGIONI.map((r) => (
                <SelectItem key={r} value={r}>{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Ordine display</Label>
            <Input
              type="number" min="0"
              value={form.ordine_display}
              onChange={(e) => setF({ ordine_display: parseInt(e.target.value, 10) || 0 })}
              data-testid="province-form-ordine"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <input
                type="checkbox"
                checked={form.attivo}
                onChange={(e) => setF({ attivo: e.target.checked })}
                data-testid="province-form-attivo"
              />
              Attiva
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
          <Button size="sm" onClick={submit} disabled={saving}
                  data-testid="province-form-save">
            <Save className="h-4 w-4 mr-1" /> {isEdit ? "Salva modifiche" : "Crea"}
          </Button>
        </div>
      </Card>

      {confirmOpen && (
        <SistemaConfirmModal
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doSubmit}
          saving={saving}
        />
      )}
    </div>
  );
}


function SistemaConfirmModal({ onCancel, onConfirm, saving }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
         onClick={onCancel}
         data-testid="sistema-confirm-modal">
      <Card className="w-[440px] p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b pb-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h3 className="text-lg font-bold">Conferma modifica</h3>
        </div>
        <p className="text-sm text-slate-700">
          Sei sicuro di voler modificare un <strong>record di sistema</strong>?
          Questa azione è registrata nell'audit log.
        </p>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onCancel}
                  data-testid="sistema-confirm-cancel">
            Annulla
          </Button>
          <Button size="sm" onClick={onConfirm} disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="sistema-confirm-yes">
            Sì, modifica
          </Button>
        </div>
      </Card>
    </div>
  );
}


/* ===================================================================
 * Codici IVA
 * =================================================================== */
function CodiciIvaSection({ isDev }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("__all");
  const [includeDisattive, setIncludeDisattive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (filterTipo && filterTipo !== "__all") params.set("tipo", filterTipo);
      params.set("attivo", includeDisattive ? "all" : "true");
      const r = await api.get(`/master-data/codici-iva?${params}`);
      setList(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento codici IVA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterTipo, includeDisattive]);

  const openCreate = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (row) => { setEditing(row); setModalOpen(true); };

  const remove = async (row) => {
    if (row.is_sistema) {
      toast.error("Codice di sistema: usa 'Modifica' per disattivare.");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Disattivare il codice IVA ${row.codice}?`)) return;
    try {
      await api.delete(`/master-data/codici-iva/${row.codice}`);
      toast.success(`Codice ${row.codice} disattivato`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore disattivazione");
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/master-data/codici-iva/export/csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "codici_iva.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore export CSV");
    }
  };

  return (
    <Card className="p-5" data-testid="codici-iva-section">
      <div className="flex items-center justify-between mb-3 pb-2 border-b">
        <h3 className="text-lg font-bold">Codici IVA ({list.length})</h3>
        <div className="flex flex-wrap gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca codice o descrizione…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[240px]"
              data-testid="iva-search"
            />
            {search && (
              <button onClick={() => setSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-[180px]" data-testid="iva-filter-tipo">
              <SelectValue placeholder="Filtra per tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">Tutti i tipi</SelectItem>
              {IVA_TIPI.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer px-2">
            <input type="checkbox" checked={includeDisattive}
                   onChange={(e) => setIncludeDisattive(e.target.checked)}
                   data-testid="iva-toggle-disattive" />
            Mostra disattivati
          </label>
          <Button size="sm" variant="outline" onClick={exportCsv}
                  data-testid="iva-export-csv">
            <Download className="h-4 w-4 mr-1" /> Esporta CSV
          </Button>
          <Button size="sm" onClick={openCreate}
                  data-testid="iva-add">
            <Plus className="h-4 w-4 mr-1" /> Nuovo codice
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-400">Caricamento…</div>
      ) : (
        <table className="w-full text-sm">
          <thead className={THEAD_WRAPPER}>
            <tr>
              <th className={`${COL_HEADER_LEFT} w-[100px]`}>Codice</th>
              <th className={COL_HEADER_LEFT}>Descrizione</th>
              <th className={`${COL_HEADER_LEFT} w-[120px]`}>Tipo</th>
              <th className={`${COL_HEADER_CENTER} w-[90px]`}>Aliquota</th>
              <th className={`${COL_HEADER_CENTER} w-[100px]`}>Natura SdI</th>
              <th className={`${COL_HEADER_CENTER} w-[90px]`}>Attivo</th>
              <th className={`${COL_HEADER_CENTER} w-[80px]`}>Sistema</th>
              <th className="w-[80px]" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.map((r) => (
              <tr key={r.codice} data-testid={`iva-row-${r.codice}`}
                  className={r.attivo ? "" : "opacity-60"}>
                <td className={`${CELL_TEXT_DEFAULT} font-mono font-semibold`}>{r.codice}</td>
                <td className={CELL_TEXT_DEFAULT}>{r.descrizione}</td>
                <td className={`${CELL_TEXT_DEFAULT} text-slate-600`}>
                  {IVA_TIPI.find((t) => t.value === r.tipo)?.label || r.tipo}
                </td>
                <td className={`${CELL_TEXT_DEFAULT} text-center font-mono`}>
                  {r.aliquota}%
                </td>
                <td className={`${CELL_TEXT_DEFAULT} text-center text-slate-500`}>
                  {r.natura_sdi || "—"}
                </td>
                <td className={`${CELL_TEXT_DEFAULT} text-center`}>
                  {r.attivo
                    ? <span className="text-emerald-600">●</span>
                    : <span className="text-slate-400">○</span>}
                </td>
                <td className={`${CELL_TEXT_DEFAULT} text-center`}>
                  {r.is_sistema
                    ? <Lock className="h-3.5 w-3.5 text-slate-500 inline" />
                    : <span className="text-slate-300">—</span>}
                </td>
                <td className={CELL_AZIONI}>
                  <button onClick={() => openEdit(r)}
                          className={ICON_BTN_EDIT}
                          title="Modifica"
                          data-testid={`iva-edit-${r.codice}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(r)}
                          disabled={r.is_sistema}
                          className={`${ICON_BTN_DELETE} ${r.is_sistema ? "opacity-30 cursor-not-allowed" : ""}`}
                          title={r.is_sistema ? "Codice di sistema: non eliminabile" : "Disattiva"}
                          data-testid={`iva-delete-${r.codice}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-slate-400 italic">
                Nessun codice IVA trovato.
              </td></tr>
            )}
          </tbody>
        </table>
      )}

      {modalOpen && (
        <CodiceIvaModal
          row={editing}
          isDev={isDev}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); load(); }}
        />
      )}
    </Card>
  );
}


function CodiceIvaModal({ row, isDev, onClose, onSaved }) {
  const isEdit = !!row;
  const isSistema = !!row?.is_sistema;
  // Sprint 4I1.A.1: developer può modificare TUTTI i campi anche su record sistema
  const canEditAllFields = !isSistema || isDev;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form, setForm] = useState({
    codice: row?.codice || "",
    descrizione: row?.descrizione || "",
    aliquota: row?.aliquota ?? 0,
    natura_sdi: row?.natura_sdi || "",
    tipo: row?.tipo || "imponibile",
    attivo: row?.attivo ?? true,
    ordine_display: row?.ordine_display ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const requiresExtraConfirm = isEdit && isSistema && isDev;

  const doSubmit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        let payload;
        if (isSistema && isDev) {
          payload = {
            descrizione: form.descrizione,
            aliquota: parseFloat(form.aliquota) || 0,
            natura_sdi: form.natura_sdi || null,
            tipo: form.tipo,
            attivo: form.attivo,
            ordine_display: form.ordine_display,
          };
          if (form.codice && form.codice !== row.codice) {
            payload.codice = form.codice;
          }
        } else if (isSistema) {
          payload = { attivo: form.attivo, ordine_display: form.ordine_display };
        } else {
          payload = {
            descrizione: form.descrizione,
            aliquota: parseFloat(form.aliquota) || 0,
            natura_sdi: form.natura_sdi || null,
            tipo: form.tipo,
            attivo: form.attivo,
            ordine_display: form.ordine_display,
          };
        }
        await api.patch(`/master-data/codici-iva/${row.codice}`, payload);
        toast.success(`Codice ${row.codice} aggiornato`);
      } else {
        if (!form.codice.trim() || !form.descrizione.trim()) {
          toast.error("Compila codice e descrizione");
          setSaving(false);
          return;
        }
        await api.post("/master-data/codici-iva", {
          ...form,
          aliquota: parseFloat(form.aliquota) || 0,
          natura_sdi: form.natura_sdi || null,
        });
        toast.success(`Codice ${form.codice} creato`);
      }
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Errore salvataggio");
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const submit = async () => {
    if (requiresExtraConfirm) {
      setConfirmOpen(true);
      return;
    }
    await doSubmit();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}
         data-testid="iva-modal">
      <Card className="w-[520px] p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b pb-2">
          <h3 className="text-lg font-bold">
            {isEdit ? "Modifica codice IVA" : "Nuovo codice IVA"}
          </h3>
          {isSistema && (
            <span className="text-[11px] text-slate-500 flex items-center gap-1">
              <Lock className="h-3 w-3" /> Sistema
            </span>
          )}
        </div>

        {isSistema && isDev && (
          <div className={WARNING_BANNER_CN} data-testid="iva-modal-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <strong>Stai modificando un record di sistema.</strong> Le modifiche
              impatteranno tutti i record collegati a questo codice. Procedi con cautela.
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Codice *</Label>
            <Input
              value={form.codice}
              onChange={(e) => setF({ codice: e.target.value.slice(0, 10) })}
              placeholder="I22"
              disabled={isEdit && !isDev}
              maxLength={10}
              data-testid="iva-form-codice"
              className="font-mono"
            />
            {isEdit && !isDev && (
              <p className="text-[11px] text-slate-400 mt-0.5">Non modificabile</p>
            )}
            {isEdit && isSistema && isDev && (
              <p className="text-[11px] text-amber-700 mt-0.5">Rename consentito (developer)</p>
            )}
          </div>
          <div className="col-span-2">
            <Label className="text-xs">Descrizione *</Label>
            <Input
              value={form.descrizione}
              onChange={(e) => setF({ descrizione: e.target.value })}
              placeholder="22% (Cod. I22)"
              disabled={!canEditAllFields}
              data-testid="iva-form-descrizione"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Aliquota %</Label>
            <Input
              type="number" step="0.01" min="0" max="100"
              value={form.aliquota}
              onChange={(e) => setF({ aliquota: e.target.value })}
              disabled={!canEditAllFields}
              data-testid="iva-form-aliquota"
            />
          </div>
          <div>
            <Label className="text-xs">Natura SdI</Label>
            <Input
              value={form.natura_sdi}
              onChange={(e) => setF({ natura_sdi: e.target.value.toUpperCase().slice(0, 10) })}
              placeholder="N5 (opzionale)"
              disabled={!canEditAllFields}
              data-testid="iva-form-natura"
              className="font-mono"
            />
          </div>
          <div>
            <Label className="text-xs">Tipo *</Label>
            <Select
              value={form.tipo}
              onValueChange={(v) => setF({ tipo: v })}
              disabled={!canEditAllFields}>
              <SelectTrigger data-testid="iva-form-tipo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IVA_TIPI.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Ordine display</Label>
            <Input
              type="number" min="0"
              value={form.ordine_display}
              onChange={(e) => setF({ ordine_display: parseInt(e.target.value, 10) || 0 })}
              data-testid="iva-form-ordine"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
              <input
                type="checkbox"
                checked={form.attivo}
                onChange={(e) => setF({ attivo: e.target.checked })}
                data-testid="iva-form-attivo"
              />
              Attivo
            </label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={onClose}>Annulla</Button>
          <Button size="sm" onClick={submit} disabled={saving}
                  data-testid="iva-form-save">
            <Save className="h-4 w-4 mr-1" /> {isEdit ? "Salva modifiche" : "Crea"}
          </Button>
        </div>
      </Card>

      {confirmOpen && (
        <SistemaConfirmModal
          onCancel={() => setConfirmOpen(false)}
          onConfirm={doSubmit}
          saving={saving}
        />
      )}
    </div>
  );
}


// ============================================================
// Sprint 4H-DEP.B — Sezione "Depositi di sistema"
// ============================================================
// I 2 depositi di sistema sono globali, immutabili nel codice e non
// cancellabili. Solo l'etichetta (`nome`) è modificabile via PATCH.
function DepositiSistemaSection({ elevated }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draftNome, setDraftNome] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/depositi?tipo=SISTEMA&include_sistema=true");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Errore caricamento depositi di sistema");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const beginEdit = (d) => { setEditingId(d.id); setDraftNome(d.nome); };
  const cancelEdit = () => { setEditingId(null); setDraftNome(""); };

  const save = async (id) => {
    const nome = draftNome.trim();
    if (!nome) { toast.error("Nome obbligatorio"); return; }
    setSaving(true);
    try {
      await api.patch(`/depositi/${id}`, { nome });
      toast.success("Etichetta aggiornata");
      cancelEdit();
      await load();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4" data-testid="depositi-sistema-section">
      <div className="mb-3">
        <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-500" /> Depositi di sistema
        </div>
        <p className="text-xs text-slate-500 mt-0.5">
          Voci globali immutabili (non cancellabili, non duplicabili).
          Solo l'etichetta visibile è modificabile. Il codice tecnico è readonly.
        </p>
      </div>
      <div className={`${WARNING_BANNER_CN} mb-3`}>
        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
        <span>Questi depositi sono validi per <strong>tutte le aziende e tutte le sedi</strong>. Modifica solo l'etichetta visualizzata, non la sostanza.</span>
      </div>
      {loading ? (
        <div className="text-sm text-slate-400 italic">Caricamento…</div>
      ) : (
        <table className="w-full text-sm">
          <thead className={THEAD_WRAPPER}>
            <tr>
              <th className={COL_HEADER_LEFT}>Codice (immutabile)</th>
              <th className={COL_HEADER_LEFT}>Etichetta visibile</th>
              <th className={COL_HEADER_CENTER}>Tipo</th>
              {elevated && <th className="w-[110px]"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((d) => (
              <tr key={d.id} className="hover:bg-slate-50" data-testid={`sys-dep-row-${d.codice_sistema}`}>
                <td className={CELL_TEXT_DEFAULT}>
                  <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {d.codice_sistema}
                  </span>
                </td>
                <td className="py-1.5">
                  {editingId === d.id ? (
                    <Input
                      value={draftNome}
                      onChange={(e) => setDraftNome(e.target.value)}
                      className="h-7 text-sm"
                      autoFocus
                      data-testid={`sys-dep-input-${d.codice_sistema}`}
                    />
                  ) : (
                    <span className="text-slate-800 font-medium">{d.nome}</span>
                  )}
                </td>
                <td className="py-1.5 text-center text-[11px] uppercase tracking-wider text-slate-500">
                  Sistema
                </td>
                {elevated && (
                  <td className={CELL_AZIONI}>
                    {editingId === d.id ? (
                      <>
                        <button onClick={() => save(d.id)} disabled={saving}
                                className={ICON_BTN_EDIT} title="Salva"
                                data-testid={`sys-dep-save-${d.codice_sistema}`}>
                          <Save className="h-4 w-4" />
                        </button>
                        <button onClick={cancelEdit} className={ICON_BTN_DELETE}
                                title="Annulla">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <button onClick={() => beginEdit(d)} className={ICON_BTN_EDIT}
                              title="Rinomina etichetta"
                              data-testid={`sys-dep-edit-${d.codice_sistema}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
